import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useEnhancedToast } from "@/components/ui/enhanced-toast";
import { 
    Globe, 
    Hash, 
    Key,
    Phone, 
    Server, 
    User, 
    Calendar, 
    Copy, 
    Check, 
    Users,
    AlertCircle,
    CheckCircle2
} from "lucide-react";
import { useState, useEffect, useId, useRef } from "react";
import { Combobox } from "@/components/ui/combobox";
import type { PhoneNumberDetails as PhoneNumberDetailsType } from "@/api/phone-numbers/phone_numbers.types";
import { PhoneInput } from '@/components/ui/phone-input';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Form, FormControl, FormField, FormItem, FormMessage, FormLabel } from '@/components/ui/form';
import { updatePhoneNumber } from "@/api/phone-numbers/phone_numbers.api";
import type { Assistant } from "@/api/assistants/assistants.types";
import type { MultiAgent } from '@/api/multi-agent/multi_agent.types';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useAuth } from '@/context/AuthContext';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

const phoneDetailsSchema = z.object({
    name: z.string().max(40, 'Display name must be 40 characters or less'),
    serverUrl: z.string()
        .refine(
            (url) => !url || url.match(/^https:\/\/(dev\.)?(workflow\.(amira\.bot|voicebot\.studio))\/api\/v1\/webhooks\/[a-zA-Z0-9]+\/?(?:sync)?$/),
            'Only Our Workflow webhook URLs are supported (e.g., https://workflow.voicebot.studio/api/v1/webhooks/...)'
        ),
    serverUrlSecret: z.string().optional(),
    fallbackNumber: z.string()
        .refine((val) => !val || (val.startsWith('+') && isValidPhoneNumber(val)), {
            message: 'Please enter a valid phone number with country code (e.g., +49...)'
        }),
    assistantId: z.string().optional(),
    multiAgent: z.string().optional()
});

type PhoneDetailsFormValues = z.infer<typeof phoneDetailsSchema>;

interface InboundTabProps {
    formRef?: React.RefObject<HTMLFormElement>;
    phoneNumber: PhoneNumberDetailsType;
    assistants: Assistant[];
    multiAgents: MultiAgent[];
    onUpdate?: () => void;
}

export const InboundTab = ({ formRef, phoneNumber, assistants, multiAgents, onUpdate }: InboundTabProps) => {
    const { error, success } = useEnhancedToast();
    const { user } = useAuth();
    const canEditPhoneNumber = user?.permissions?.phone_numbers === 'EDITOR';
    const [showCopied, setShowCopied] = useState(false);
    const [showIdCopied, setShowIdCopied] = useState(false);

    // Create refs for toast functions
    const errorRef = useRef(error);
    const successRef = useRef(success);

    // Update refs when toast functions change
    useEffect(() => {
        errorRef.current = error;
        successRef.current = success;
    }, [error, success]);

    const form = useForm<PhoneDetailsFormValues>({
        resolver: zodResolver(phoneDetailsSchema),
        defaultValues: {
            name: phoneNumber?.name ?? '',
            // Remove /sync from the pre-filled server URL
            serverUrl: phoneNumber?.serverUrl ? phoneNumber.serverUrl.replace(/\/sync\/?$/, '') : '',
            serverUrlSecret: phoneNumber?.serverUrlSecret ?? '',
            fallbackNumber: phoneNumber?.fallbackDestination?.type === 'number' ? phoneNumber.fallbackDestination.number : '',
            assistantId: phoneNumber?.assistantId ?? '',
            multiAgent: phoneNumber?.squadId ?? ''
        }
    });

    // Generate random ID for the display name field
    const displayNameId = useId();

    useEffect(() => {
        if (phoneNumber) {
            form.reset({
                name: phoneNumber.name ?? '',
                serverUrl: phoneNumber.serverUrl ? phoneNumber.serverUrl.replace(/\/sync\/?$/, '') : '',
                serverUrlSecret: phoneNumber.serverUrlSecret ?? '',
                fallbackNumber: phoneNumber.fallbackDestination?.type === 'number' ? phoneNumber.fallbackDestination.number : '',
                assistantId: phoneNumber.assistantId ?? '',
                multiAgent: phoneNumber.squadId ?? ''
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phoneNumber]);

    const handleCopyNumber = () => {
        if (phoneNumber?.number) {
            navigator.clipboard.writeText(phoneNumber.number);
            setShowCopied(true);
            setTimeout(() => {
                setShowCopied(false);
            }, 2000);
        }
    };

    const handleCopyId = () => {
        if (phoneNumber?.id) {
            navigator.clipboard.writeText(phoneNumber.id);
            setShowIdCopied(true);
            setTimeout(() => {
                setShowIdCopied(false);
            }, 2000);
        }
    };

    const onSubmit = async (data: PhoneDetailsFormValues) => {
        if (!phoneNumber) return;

        if (!canEditPhoneNumber) {
            errorRef.current({
                icon: AlertCircle,
                title: "Permission Denied",
                description: "You need editor permissions to save changes to phone numbers",
                duration: 4000,
            });
            return;
        }

        // Validate URL format
        const serverUrl = data.serverUrl;
        if (serverUrl && !serverUrl.match(/^https:\/\/(dev\.)?(workflow\.(amira\.bot|voicebot\.studio))\/api\/v1\/webhooks\/[a-zA-Z0-9]+\/?(?:sync)?$/)) {
            errorRef.current({
                icon: AlertCircle,
                title: "Invalid Server URL",
                description: "Only Our Workflow webhook URLs are supported. Please use a valid workflow URL.",
                duration: 4000,
            });
            return;
        }

        try {
            const updateData: {
                name: string;
                serverUrl?: string;
                serverUrlSecret?: string;
                assistantId?: string | undefined;
                squadId?: string | undefined;
                fallbackDestination?: {
                    type: 'number';
                    number: string;
                } | undefined;
            } = {
                name: data.name,
                // Ensure /sync is added to the URL if not already present
                serverUrl: data.serverUrl ? `${data.serverUrl.replace(/\/sync\/?$/, '')}/sync` : undefined,
                serverUrlSecret: data.serverUrlSecret || undefined,
            };

            // Handle assistantId
            if (data.assistantId) {
                updateData.assistantId = data.assistantId;
            } else {
                updateData.assistantId = undefined;
            }

            // Handle multiAgent
            if (data.multiAgent) {
                updateData.squadId = data.multiAgent;
            } else {
                updateData.squadId = undefined;
            }

            // Handle fallback number
            if (data.fallbackNumber) {
                updateData.fallbackDestination = {
                    type: 'number',
                    number: data.fallbackNumber,
                };
            } else {
                updateData.fallbackDestination = undefined;
            }

            const response = await updatePhoneNumber(phoneNumber.id, updateData);

            if (response.error) {
                throw new Error(response.error);
            }

            successRef.current({
                icon: CheckCircle2,
                title: "Changes Saved",
                description: "Phone number settings have been updated successfully.",
                duration: 4000,
            });

            if (onUpdate) {
                onUpdate();
            }
        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Update Failed",
                description: err instanceof Error ? err.message : "Could not update phone number settings",
                duration: 4000,
            });
        }
    };

    return (
        <div className="space-y-4">
            {/* Info Cards - Grid layout for desktop - Only visible for BYO phone numbers */}
            {/* COMMENTED OUT: SIP Origination URI and IP Access Control banners */}
            {/* {phoneNumber.provider === 'byo-phone-number' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="border bg-muted/30">
                        <div className="p-4">
                            <div className="flex flex-col space-y-3">
                                <div className="flex items-start gap-2">
                                    <Network className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                    <div className="space-y-0.5 flex-1">
                                        <Label className="text-sm font-medium">
                                            SIP Origination URI
                                        </Label>
                                        <p className="text-xs text-muted-foreground">
                                            Configure at your SIP provider
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="flex items-center gap-2 ml-6">
                                    <div className="flex-1 font-mono text-sm bg-background border rounded-md px-3 py-2 overflow-x-auto">
                                        sip:4d4be5b5-dfdf-3cf4-aa60-eabce7dbcc83.sip.cloudonix.net
                                    </div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={handleCopyOriginUri}
                                        className="h-9 w-9 flex-shrink-0"
                                        title={showOriginUriCopied ? "Copied!" : "Copy URI"}
                                    >
                                        {showOriginUriCopied ? (
                                            <Check className="h-4 w-4 text-green-500" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Card>

                    <Card className="border bg-muted/30">
                        <div className="p-4">
                            <div className="flex flex-col space-y-3">
                                <div className="flex items-start gap-2">
                                    <Server className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                    <div className="space-y-0.5 flex-1">
                                        <Label className="text-sm font-medium">
                                            IP Access Control
                                        </Label>
                                        <p className="text-xs text-muted-foreground">
                                            Add this IP to your provider's allowlist
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="flex items-center gap-2 ml-6">
                                    <div className="flex-1 font-mono text-sm bg-background border rounded-md px-3 py-2">
                                        18.219.128.166
                                    </div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => {
                                            navigator.clipboard.writeText("18.219.128.166");
                                            setShowIpCopied(true);
                                            setTimeout(() => {
                                                setShowIpCopied(false);
                                            }, 2000);
                                        }}
                                        className="h-9 w-9 flex-shrink-0"
                                        title={showIpCopied ? "Copied!" : "Copy IP"}
                                    >
                                        {showIpCopied ? (
                                            <Check className="h-4 w-4 text-green-500" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
            )} */}

            <Form {...form}>
                <form 
                    ref={formRef}
                    id="phone-details-form" 
                    onSubmit={form.handleSubmit(onSubmit)} 
                    className="grid grid-cols-1 lg:grid-cols-2 gap-6"
                    autoComplete="off"
                >
                    {/* Basic Information Card */}
                    <Card className="shadow-sm lg:col-span-2">
                        <CardHeader className="border-b bg-muted/20">
                            <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                    <Phone className="h-4 w-4 text-primary" />
                                </div>
                                <div className="flex-1 space-y-1">
                                    <CardTitle>Basic Information</CardTitle>
                                    <CardDescription>Phone number details and identification</CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Phone Number and ID Section */}
                            <div className="grid md:grid-cols-2 gap-6">
                                {/* Phone Number */}
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Phone className="h-4 w-4 text-primary" />
                                        Phone Number
                                    </Label>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            value={phoneNumber.number}
                                            readOnly
                                            className="h-9 font-mono text-sm flex-1 bg-muted/50"
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={handleCopyNumber}
                                            className="shrink-0"
                                        >
                                            {showCopied ? (
                                                <Check className="h-4 w-4 text-green-500" />
                                            ) : (
                                                <Copy className="h-4 w-4" />
                                            )}
                                        </Button>
                                    </div>
                                </div>

                                {/* ID Field */}
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Hash className="h-4 w-4 text-primary" />
                                        ID
                                    </Label>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            value={phoneNumber.id}
                                            readOnly
                                            className="h-9 font-mono text-sm flex-1 bg-muted/50"
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={handleCopyId}
                                            className="shrink-0"
                                        >
                                            {showIdCopied ? (
                                                <Check className="h-4 w-4 text-green-500" />
                                            ) : (
                                                <Copy className="h-4 w-4" />
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Display Name */}
                            <div className="grid md:grid-cols-4 gap-6">
                                <div className="md:col-span-2">
                                    <FormField
                                        control={form.control}
                                        name="name"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="flex items-center gap-2">
                                                    <User className="h-4 w-4 text-primary" />
                                                    Display Name
                                                </FormLabel>
                                                <FormControl>
                                                    <Input 
                                                        {...field} 
                                                        id={displayNameId}
                                                        name={`custom_field_${displayNameId}`}
                                                        placeholder="Enter a name for this number"
                                                        autoComplete="off"
                                                        data-lpignore="true"
                                                        data-form-type="other"
                                                        value={field.value || ''}
                                                        onFocus={(e) => e.target.setAttribute('autocomplete', 'chrome-off')}
                                                        className="h-9"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                {/* Provider */}
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Server className="h-4 w-4 text-primary" />
                                        Provider
                                    </Label>
                                    <Input
                                        value={phoneNumber.provider === 'byo-phone-number' ? 'SIP' : phoneNumber.provider}
                                        readOnly
                                        className="h-9 text-sm bg-muted/50 capitalize"
                                    />
                                </div>

                                {/* Created Date */}
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Calendar className="h-4 w-4 text-primary" />
                                        Created
                                    </Label>
                                    <Input
                                        value={new Date(phoneNumber.createdAt).toLocaleDateString()}
                                        readOnly
                                        className="h-9 text-sm bg-muted/50"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Server Configuration Card */}
                    <Card className="shadow-sm lg:col-span-2">
                        <CardHeader className="border-b bg-muted/20">
                            <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                    <Globe className="h-4 w-4 text-primary" />
                                </div>
                                <div className="flex-1 space-y-1">
                                    <CardTitle>Server Configuration</CardTitle>
                                    <CardDescription>Optional webhook server settings</CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid md:grid-cols-2 gap-6">
                                    {/* Server URL */}
                                    <FormField
                                        control={form.control}
                                        name="serverUrl"
                                        render={({ field }) => (
                                            <FormItem>
                                                <div className="flex items-center gap-2">
                                                    <FormLabel className="flex items-center gap-2">
                                                        <Globe className="h-4 w-4 text-primary" />
                                                        Server URL
                                                    </FormLabel>
                                                    <TooltipProvider delayDuration={200}>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <AlertCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                                                            </TooltipTrigger>
                                                            <TooltipContent className="max-w-sm space-y-2 p-3">
                                                                <p className="text-sm font-medium">Workflow Webhook URL</p>
                                                                <p className="text-xs text-muted-foreground">
                                                                    Enter your workflow webhook URL. The /sync suffix is automatically appended for synchronous responses (30s timeout).
                                                                </p>
                                                                <p className="text-xs text-muted-foreground">
                                                                    Only workflow URLs from workflow.voicebot.studio or workflow.amira.bot are supported.
                                                                </p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                </div>
                                                <FormControl>
                                                    <div className="relative">
                                                        <Input
                                                            {...field}
                                                            placeholder="https://workflow.voicebot.studio/api/v1/webhooks/..."
                                                            className="h-9 font-mono pr-[60px]"
                                                            type="url"
                                                            autoComplete="off"
                                                            spellCheck="false"
                                                            onChange={(e) => {
                                                                const value = e.target.value.replace(/\/sync\/?$/, '');
                                                                field.onChange(value);
                                                            }}
                                                        />
                                                        <div className="absolute right-0 top-0 bottom-0 flex items-center px-3 pointer-events-none border-l bg-muted/40">
                                                            <span className="text-xs font-mono text-muted-foreground">/sync</span>
                                                        </div>
                                                    </div>
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    {/* Server URL Secret */}
                                    <FormField
                                        control={form.control}
                                        name="serverUrlSecret"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="flex items-center gap-2">
                                                    <Key className="h-4 w-4 text-primary" />
                                                    Server URL Secret
                                                </FormLabel>
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        placeholder="Enter your server URL secret"
                                                        className="h-9 font-mono"
                                                        type="password"
                                                        autoComplete="new-password"
                                                        spellCheck="false"
                                                    />
                                                </FormControl>
                                                <p className="text-xs text-muted-foreground">
                                                    Secret key for webhook verification
                                                </p>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                        </CardContent>
                    </Card>

                    {/* Call Routing Card */}
                    <Card className="shadow-sm">
                        <CardHeader className="border-b bg-muted/20">
                            <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                    <Users className="h-4 w-4 text-primary" />
                                </div>
                                <div className="flex-1 space-y-1">
                                    <CardTitle>Call Routing</CardTitle>
                                    <CardDescription>Configure assistant and multi-agent routing</CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Assistant Selection */}
                            <FormField
                                control={form.control}
                                name="assistantId"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="flex items-center gap-2">
                                            <User className="h-4 w-4 text-primary" />
                                            Assistant
                                        </FormLabel>
                                        <FormControl>
                                            <Combobox
                                                options={assistants.map(a => ({
                                                    value: a.id,
                                                    label: a.name,
                                                    description: a.id
                                                }))}
                                                value={field.value ?? ""}
                                                onValueChange={(value) => {
                                                    // Use setValue instead of field.onChange to force the update
                                                    form.setValue('assistantId', value === undefined ? "" : value, {
                                                        shouldValidate: true,
                                                        shouldDirty: true,
                                                    });
                                                }}
                                                placeholder="Select an assistant..."
                                                searchPlaceholder="Search assistant..."
                                                emptyMessage="No assistant found."
                                                allowClear={true}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Multi Agent Selection */}
                            <FormField
                                control={form.control}
                                name="multiAgent"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="flex items-center gap-2">
                                            <Users className="h-4 w-4 text-primary" />
                                            Multi Agent
                                        </FormLabel>
                                        <FormControl>
                                            <Combobox
                                                options={multiAgents.map(ma => ({
                                                    value: ma.id,
                                                    label: ma.name,
                                                    description: ma.id
                                                }))}
                                                value={field.value ?? ""}
                                                onValueChange={(value) => {
                                                    // Use setValue instead of field.onChange to force the update
                                                    form.setValue('multiAgent', value === undefined ? "" : value, {
                                                        shouldValidate: true,
                                                        shouldDirty: true,
                                                    });
                                                }}
                                                placeholder="Select a multi agent..."
                                                searchPlaceholder="Search multi agent..."
                                                emptyMessage="No multi agent found."
                                                allowClear={true}
                                            />
                                        </FormControl>
                                        <p className="text-xs text-muted-foreground">
                                            Fallback routing when assistant is unavailable
                                        </p>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </CardContent>
                    </Card>

                    {/* Call Forwarding Card */}
                    <Card className="shadow-sm">
                        <CardHeader className="border-b bg-muted/20">
                            <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                    <Phone className="h-4 w-4 text-primary" />
                                </div>
                                <div className="flex-1 space-y-1">
                                    <CardTitle>Call Forwarding</CardTitle>
                                    <CardDescription>Optional fallback number for call forwarding</CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Fallback Destination */}
                            <FormField
                                control={form.control}
                                name="fallbackNumber"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="flex items-center gap-2">
                                            <Phone className="h-4 w-4 text-primary" />
                                            Fallback Destination
                                        </FormLabel>
                                        <FormControl>
                                            <PhoneInput
                                                value={field.value ?? undefined}
                                                onChange={(value) => {
                                                    field.onChange(value ? value : '');
                                                }}
                                                defaultCountry="DE"
                                                international
                                            />
                                        </FormControl>
                                        <p className="text-xs text-muted-foreground">
                                            Forward calls when assistant or multi-agent unavailable
                                        </p>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </CardContent>
                    </Card>
                </form>
            </Form>
        </div>
    );
};
